import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import {
  HiOutlineServer, HiOutlineRefresh,
  HiOutlineChevronDown, HiOutlineClock,
  HiOutlineChartBar, HiOutlineDatabase,
  HiOutlinePhotograph, HiOutlineArchive,
  HiOutlineCheck, HiOutlineX, HiOutlineTrash, HiOutlinePlus, HiOutlineDownload
} from 'react-icons/hi';
import { getVMInstances, getVMMetrics, getBuckets, getBucketLatestFile, getDiskSnapshots, getObservabilityConfig, saveObservabilityConfig } from '../services/api';
import { TIME_PRESETS, getPresetRange, toLocalDatetimeString } from '../shared/constants';
import selectStyles from '../shared/selectStyles';
import './MongoObservability.css';

const METRIC_COLORS = {
  'CPU Utilization':                          { stroke: '#10b981', fill: 'rgba(16,185,129,0.12)', gradient: ['rgba(16,185,129,0.3)', 'rgba(16,185,129,0.02)'] },
  'Memory Utilization':                       { stroke: '#8b5cf6', fill: 'rgba(139,92,246,0.12)', gradient: ['rgba(139,92,246,0.3)', 'rgba(139,92,246,0.02)'] },
  '/dev/sda1 (Primary Disk) Utilization':     { stroke: '#f59e0b', fill: 'rgba(245,158,11,0.12)', gradient: ['rgba(245,158,11,0.3)', 'rgba(245,158,11,0.02)'] },
  '/dev/sdb (Secondary Disk) Utilization':    { stroke: '#06b6d4', fill: 'rgba(6,182,212,0.12)', gradient: ['rgba(6,182,212,0.3)', 'rgba(6,182,212,0.02)'] },
};

const GCE_ZONES = [
  'us-central1','us-east1','us-east4','us-west1','us-west2','us-west3','us-west4',
  'europe-west1','europe-west2','europe-west3','europe-west4','europe-west6','europe-north1',
  'asia-south1','asia-south2','asia-southeast1','asia-southeast2','asia-east1','asia-east2','asia-northeast1','asia-northeast2','asia-northeast3',
  'australia-southeast1','australia-southeast2',
  'southamerica-east1','northamerica-northeast1','northamerica-northeast2',
  'me-west1','me-central1','africa-south1',
];

const DISK_LABELS = {
  '/dev/sda1': '/dev/sda1 (Primary Disk)',
  '/dev/sdb': '/dev/sdb (Secondary Disk)',
};

function MongoObservability({ gcpProjects, selectedProjectId }) {
  const [configLoaded, setConfigLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState('vm-metrics');

  const [showVMModal, setShowVMModal] = useState(false);
  const [showBucketModal, setShowBucketModal] = useState(false);

  const [selectedZone, setSelectedZone] = useState(null);
  const [instances, setInstances] = useState([]);
  const [instancePick, setInstancePick] = useState(null);
  const [loadingInstances, setLoadingInstances] = useState(false);

  // Multi-VM state
  const [vmInstances, setVmInstances] = useState([]);
  const [expandedVMs, setExpandedVMs] = useState({});
  const [vmMetricsMap, setVmMetricsMap] = useState({});
  const [vmDiskMap, setVmDiskMap] = useState({});
  const [vmLoadingMap, setVmLoadingMap] = useState({});
  const [vmTimeRangeMap, setVmTimeRangeMap] = useState({});

  // Multi-bucket state
  const [addedBuckets, setAddedBuckets] = useState([]);
  const [expandedBuckets, setExpandedBuckets] = useState({});
  const [bucketLatestMap, setBucketLatestMap] = useState({});
  const [bucketLatestLoadingMap, setBucketLatestLoadingMap] = useState({});

  // Bucket picker
  const [buckets, setBuckets] = useState([]);
  const [bucketsLoading, setBucketsLoading] = useState(false);
  const [bucketPick, setBucketPick] = useState(null);

  const [snapshots, setSnapshots] = useState(null);
  const [snapshotsLoading, setSnapshotsLoading] = useState(false);
  const [snapshotDiskFilter, setSnapshotDiskFilter] = useState('');

  const [timePreset, setTimePreset] = useState('1h');
  const [showTimePresets, setShowTimePresets] = useState(false);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const presetsRef = useRef(null);

  const activeProject = useMemo(() => {
    if (!selectedProjectId || selectedProjectId === 'all') return null;
    return gcpProjects.find((p) => p._id === selectedProjectId) || null;
  }, [selectedProjectId, gcpProjects]);

  const zoneOptions = GCE_ZONES.map((z) => ({ value: z, label: z }));

  const instanceOptions = useMemo(() => {
    const addedIds = new Set(vmInstances.map(v => v.id || v.value));
    return instances
      .filter(inst => !addedIds.has(inst.id || inst.name))
      .map((inst) => ({
        value: inst.id || inst.name,
        label: `${inst.name} (${inst.machineType || 'unknown'}) — ${inst.zone} — ${inst.status}`,
        name: inst.name,
        ...inst,
      }));
  }, [instances, vmInstances]);

  useEffect(() => {
    const handler = (e) => {
      if (presetsRef.current && !presetsRef.current.contains(e.target)) setShowTimePresets(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    setInstances([]);
    setInstancePick(null);
    setVmMetricsMap({});
    setVmDiskMap({});
    setVmLoadingMap({});
    setVmTimeRangeMap({});
    setBucketLatestMap({});
    setBucketLatestLoadingMap({});
    setBuckets([]);
    setBucketPick(null);
    setSnapshots(null);
    setSnapshotDiskFilter('');
    setConfigLoaded(false);

    if (!selectedProjectId || selectedProjectId === 'all') {
      setSelectedZone(null);
      setVmInstances([]);
      setExpandedVMs({});
      setAddedBuckets([]);
      setExpandedBuckets({});
      setActiveTab('vm-metrics');
      setConfigLoaded(true);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await getObservabilityConfig(selectedProjectId);
        if (cancelled) return;
        const cfg = res.data;
        setSelectedZone(cfg.selectedZone || null);

        const vms = cfg.vmInstances || (cfg.vmInstance ? [cfg.vmInstance] : []);
        setVmInstances(vms);
        const vmExp = {};
        if (vms.length > 0) vmExp[vms[0].id || vms[0].value || 0] = true;
        setExpandedVMs(vmExp);

        const bkts = cfg.buckets || (cfg.bucket ? [cfg.bucket] : []);
        setAddedBuckets(bkts);
        const bktExp = {};
        if (bkts.length > 0) bktExp[bkts[0].value || 0] = true;
        setExpandedBuckets(bktExp);

        setActiveTab(cfg.activeTab || 'vm-metrics');
      } catch {
        if (!cancelled) {
          setSelectedZone(null);
          setVmInstances([]);
          setExpandedVMs({});
          setAddedBuckets([]);
          setExpandedBuckets({});
          setActiveTab('vm-metrics');
        }
      } finally {
        if (!cancelled) setConfigLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedProjectId]);

  useEffect(() => {
    if (!configLoaded || !activeProject || !selectedZone) { setInstances([]); setInstancePick(null); return; }
    let cancelled = false;
    (async () => {
      setLoadingInstances(true);
      setInstances([]);
      setInstancePick(null);
      try {
        const res = await getVMInstances(activeProject._id, selectedZone.value);
        if (!cancelled) setInstances(res.data);
      } catch (err) {
        if (!cancelled) toast.error('Failed to load instances: ' + (err.response?.data?.error || err.message));
      } finally {
        if (!cancelled) setLoadingInstances(false);
      }
    })();
    return () => { cancelled = true; };
  }, [configLoaded, activeProject, selectedZone]);

  const getTimeRange = useCallback(() => {
    if (timePreset === 'custom') {
      return { startTime: customStart, endTime: customEnd };
    }
    const preset = TIME_PRESETS.find((p) => p.value === timePreset);
    if (preset) return getPresetRange(preset.hours);
    return {};
  }, [timePreset, customStart, customEnd]);

  const fetchMetricsForVM = useCallback(async (vm) => {
    if (!activeProject || !vm) return;
    const vmKey = vm.id || vm.value;
    setVmLoadingMap(prev => ({ ...prev, [vmKey]: true }));
    try {
      const { startTime, endTime } = getTimeRange();
      const res = await getVMMetrics(activeProject._id, vm.id || vm.value, vm.zone, { startTime, endTime });
      setVmMetricsMap(prev => ({ ...prev, [vmKey]: res.data.metrics }));
      setVmDiskMap(prev => ({ ...prev, [vmKey]: res.data.diskSummary }));
      setVmTimeRangeMap(prev => ({ ...prev, [vmKey]: res.data.timeRange }));
    } catch (err) {
      toast.error(`Failed to fetch metrics for ${vm.name}: ` + (err.response?.data?.error || err.message));
    } finally {
      setVmLoadingMap(prev => ({ ...prev, [vmKey]: false }));
    }
  }, [activeProject, getTimeRange]);

  useEffect(() => {
    if (!configLoaded || !activeProject || vmInstances.length === 0) return;
    vmInstances.forEach(vm => fetchMetricsForVM(vm));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configLoaded, activeProject, vmInstances]);

  const refreshAllVMs = useCallback(() => {
    vmInstances.forEach(vm => fetchMetricsForVM(vm));
  }, [vmInstances, fetchMetricsForVM]);

  const activePresetLabel = TIME_PRESETS.find((p) => p.value === timePreset)?.label || 'Select time';

  const fetchBuckets = useCallback(async () => {
    if (!activeProject) return;
    setBucketsLoading(true);
    try {
      const res = await getBuckets(activeProject._id);
      setBuckets(res.data);
    } catch (err) {
      toast.error('Failed to load buckets: ' + (err.response?.data?.error || err.message));
    } finally {
      setBucketsLoading(false);
    }
  }, [activeProject]);

  const fetchBucketLatest = useCallback(async (bkt) => {
    if (!activeProject) return;
    const bktKey = bkt.value;
    setBucketLatestLoadingMap(prev => ({ ...prev, [bktKey]: true }));
    try {
      const res = await getBucketLatestFile(activeProject._id, bkt.value);
      setBucketLatestMap(prev => ({ ...prev, [bktKey]: res.data }));
    } catch (err) {
      toast.error(`Failed to load bucket info for ${bkt.value}: ` + (err.response?.data?.error || err.message));
    } finally {
      setBucketLatestLoadingMap(prev => ({ ...prev, [bktKey]: false }));
    }
  }, [activeProject]);

  useEffect(() => {
    if (!configLoaded || !activeProject || addedBuckets.length === 0) return;
    addedBuckets.forEach(bkt => fetchBucketLatest(bkt));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configLoaded, activeProject, addedBuckets]);

  const fetchSnapshots = useCallback(async () => {
    if (!activeProject) return;
    setSnapshotsLoading(true);
    try {
      const res = await getDiskSnapshots(activeProject._id, snapshotDiskFilter || undefined);
      setSnapshots(res.data);
    } catch (err) {
      toast.error('Failed to load snapshots: ' + (err.response?.data?.error || err.message));
    } finally {
      setSnapshotsLoading(false);
    }
  }, [activeProject, snapshotDiskFilter]);

  useEffect(() => {
    if (configLoaded && activeTab === 'backup' && activeProject) {
      fetchBuckets();
      fetchSnapshots();
    }
  }, [configLoaded, activeTab, activeProject, fetchBuckets, fetchSnapshots]);

  const [downloading, setDownloading] = useState(null);

  const handleDownloadFile = useCallback(async (bkt) => {
    const bktKey = bkt.value;
    const latest = bucketLatestMap[bktKey];
    if (!activeProject || !latest?.latestFile?.name) return;
    setDownloading(bktKey);
    try {
      const { default: api } = await import('../services/api');
      const res = await api.get('/vm/bucket-download', {
        params: {
          projectId: activeProject._id,
          bucketName: bkt.value,
          fileName: latest.latestFile.name,
        },
        timeout: 30000,
      });
      if (res.data?.mode === 'redirect' && res.data?.url) {
        const link = document.createElement('a');
        link.href = res.data.url;
        link.download = latest.latestFile.name.split('/').pop() || 'download';
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (err) {
      toast.error('Failed to download file: ' + (err.response?.data?.error || err.message));
    } finally {
      setDownloading(null);
    }
  }, [activeProject, bucketLatestMap]);

  const bucketOptions = useMemo(() => {
    const addedNames = new Set(addedBuckets.map(b => b.value));
    return buckets
      .filter(b => !addedNames.has(b.name))
      .map((b) => ({ value: b.name, label: `${b.name} (${b.location})` }));
  }, [buckets, addedBuckets]);

  // Persist config
  const saveTimer = useRef(null);
  useEffect(() => {
    if (!selectedProjectId || selectedProjectId === 'all' || !configLoaded) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      saveObservabilityConfig(selectedProjectId, {
        vmInstances,
        buckets: addedBuckets,
        activeTab,
        selectedZone,
      }).catch(() => {});
    }, 500);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [selectedProjectId, activeTab, selectedZone, vmInstances, addedBuckets, configLoaded]);

  const handleAddVM = () => {
    if (!instancePick) return;
    setVmInstances(prev => [...prev, instancePick]);
    setExpandedVMs(prev => ({ ...prev, [instancePick.id || instancePick.value]: true }));
    setShowVMModal(false);
    setInstancePick(null);
  };

  const handleRemoveVM = (vm) => {
    const vmKey = vm.id || vm.value;
    setVmInstances(prev => prev.filter(v => (v.id || v.value) !== vmKey));
    setExpandedVMs(prev => { const n = { ...prev }; delete n[vmKey]; return n; });
    setVmMetricsMap(prev => { const n = { ...prev }; delete n[vmKey]; return n; });
    setVmDiskMap(prev => { const n = { ...prev }; delete n[vmKey]; return n; });
  };

  const handleAddBucket = () => {
    if (!bucketPick) return;
    setAddedBuckets(prev => [...prev, bucketPick]);
    setExpandedBuckets(prev => ({ ...prev, [bucketPick.value]: true }));
    setShowBucketModal(false);
    setBucketPick(null);
  };

  const handleRemoveBucket = (bkt) => {
    setAddedBuckets(prev => prev.filter(b => b.value !== bkt.value));
    setExpandedBuckets(prev => { const n = { ...prev }; delete n[bkt.value]; return n; });
    setBucketLatestMap(prev => { const n = { ...prev }; delete n[bkt.value]; return n; });
  };

  const toggleVM = (vmKey) => setExpandedVMs(prev => ({ ...prev, [vmKey]: !prev[vmKey] }));
  const toggleBucket = (bktKey) => setExpandedBuckets(prev => ({ ...prev, [bktKey]: !prev[bktKey] }));

  const fmtBytes = (bytes) => {
    if (!bytes && bytes !== 0) return '—';
    const n = Number(bytes);
    if (n >= 1073741824) return (n / 1073741824).toFixed(2) + ' GB';
    if (n >= 1048576) return (n / 1048576).toFixed(2) + ' MB';
    if (n >= 1024) return (n / 1024).toFixed(1) + ' KB';
    return n + ' B';
  };

  return (
    <div className="mongo-observability">
      <div className="pv-header">
        <div className="pv-header-left">
          <h1><HiOutlineDatabase size={26} style={{ verticalAlign: 'middle', marginRight: 8 }} />MongoDB Observability</h1>
        </div>
      </div>

      <div className="tabs">
        <button className={`tab ${activeTab === 'vm-metrics' ? 'active' : ''}`} onClick={() => setActiveTab('vm-metrics')}>
          <HiOutlineChartBar size={16} /> VM Metrics
        </button>
        <button className={`tab ${activeTab === 'backup' ? 'active' : ''}`} onClick={() => setActiveTab('backup')}>
          <HiOutlineDatabase size={16} /> Mongo Backup
        </button>
      </div>

      {activeTab === 'vm-metrics' && (
        <div className="vm-metrics-section">
          {!activeProject ? (
            <div className="empty-state" style={{ marginTop: 40 }}>
              <HiOutlineServer size={48} style={{ color: 'var(--text-muted)', marginBottom: 12 }} />
              <h3>Select a GCP Project</h3>
              <p>Use the project selector above to choose a specific GCP project</p>
            </div>
          ) : (
          <>
          <div className="resource-action-bar">
            <button className="btn btn-primary btn-sm" onClick={() => setShowVMModal(true)}>
              <HiOutlinePlus size={14} /> Add VM Instance
            </button>
          </div>

          {vmInstances.length > 0 && (
            <>
              <div className="filter-toolbar" style={{ marginTop: 8 }}>
                <div className="filter-group" ref={presetsRef} onClick={(e) => e.stopPropagation()}>
                  <div className="dropdown-trigger" onClick={() => setShowTimePresets(!showTimePresets)}>
                    <HiOutlineClock size={15} />
                    <span>{activePresetLabel}</span>
                    <HiOutlineChevronDown size={14} />
                  </div>
                  {showTimePresets && (
                    <div className="dropdown-menu time-dropdown">
                      {TIME_PRESETS.map((p) => (
                        <div key={p.value} className={`dropdown-item ${timePreset === p.value ? 'active' : ''}`} onClick={() => { setTimePreset(p.value); if (p.value !== 'custom') setShowTimePresets(false); }}>
                          {p.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="filter-spacer"></div>
                <button className="btn btn-secondary btn-sm" onClick={refreshAllVMs} disabled={Object.values(vmLoadingMap).some(Boolean)}>
                  <HiOutlineRefresh size={14} className={Object.values(vmLoadingMap).some(Boolean) ? 'spin' : ''} /> Refresh All
                </button>
              </div>

              {timePreset === 'custom' && (
                <div className="custom-time-range">
                  <div className="time-input-group">
                    <label>Start Time</label>
                    <input type="datetime-local" value={customStart ? toLocalDatetimeString(customStart) : ''} onChange={(e) => setCustomStart(e.target.value ? new Date(e.target.value).toISOString() : '')} max={toLocalDatetimeString(new Date().toISOString())} />
                  </div>
                  <div className="time-input-group">
                    <label>End Time</label>
                    <input type="datetime-local" value={customEnd ? toLocalDatetimeString(customEnd) : ''} onChange={(e) => setCustomEnd(e.target.value ? new Date(e.target.value).toISOString() : '')} max={toLocalDatetimeString(new Date().toISOString())} />
                  </div>
                  <button className="btn btn-primary btn-sm" onClick={() => { if (customStart && customEnd) refreshAllVMs(); else toast.error('Both times required'); }}>Apply</button>
                </div>
              )}
            </>
          )}

          {vmInstances.map((vm) => {
            const vmKey = vm.id || vm.value;
            const isExpanded = expandedVMs[vmKey];
            const metrics = vmMetricsMap[vmKey];
            const diskSummary = vmDiskMap[vmKey];
            const metricsLoading = vmLoadingMap[vmKey];
            const metricsTimeRange = vmTimeRangeMap[vmKey];

            return (
              <div key={vmKey} className="expandable-resource-card card">
                <div className="expandable-resource-header" onClick={() => toggleVM(vmKey)}>
                  <div className="expandable-resource-info">
                    <HiOutlineServer size={18} className="added-resource-icon" />
                    <div className="added-resource-details">
                      <span className="added-resource-name">{vm.name}</span>
                      <span className="added-resource-meta">{vm.machineType} · {vm.zone} · {vm.status || 'RUNNING'}</span>
                    </div>
                  </div>
                  <div className="expandable-resource-actions">
                    <button className="btn btn-secondary btn-sm" onClick={(e) => { e.stopPropagation(); fetchMetricsForVM(vm); }} disabled={metricsLoading} title="Refresh">
                      <HiOutlineRefresh size={14} className={metricsLoading ? 'spin' : ''} />
                    </button>
                    <button className="btn btn-danger btn-sm" onClick={(e) => { e.stopPropagation(); handleRemoveVM(vm); }} title="Remove">
                      <HiOutlineTrash size={14} />
                    </button>
                    <HiOutlineChevronDown size={16} className={`expandable-chevron ${isExpanded ? 'expanded' : ''}`} />
                  </div>
                </div>

                {isExpanded && (
                  <div className="expandable-resource-body">
                    <div className="disk-summary-row">
                      {['/dev/sda1', '/dev/sdb'].map((dev) => {
                        const d = diskSummary?.[dev];
                        const metricName = dev === '/dev/sda1' ? '/dev/sda1 (Primary Disk) Utilization' : '/dev/sdb (Secondary Disk) Utilization';
                        const metricData = metrics?.[metricName];
                        const metricPct = metricData?.points?.length > 0
                          ? metricData.points[metricData.points.length - 1].value * 100
                          : null;
                        const hasByteData = d && d.totalBytes !== null && d.totalBytes > 0;
                        const pct = hasByteData ? ((d.usedBytes / d.totalBytes) * 100) : metricPct;

                        if (pct === null) return (
                          <div key={dev} className="disk-summary-card card">
                            <div className="disk-summary-device">{DISK_LABELS[dev] || dev}</div>
                            <div className="disk-summary-na">No data available</div>
                          </div>
                        );
                        return (
                          <div key={dev} className="disk-summary-card card">
                            <div className="disk-summary-device">{DISK_LABELS[dev] || dev}</div>
                            <div className="disk-summary-bar-wrap">
                              <div className="disk-summary-bar" style={{ width: `${Math.min(pct, 100)}%`, background: pct > 80 ? 'var(--accent-red, #ef4444)' : pct > 60 ? 'var(--accent-yellow, #f59e0b)' : 'var(--accent-green, #10b981)' }}></div>
                            </div>
                            <div className="disk-summary-stats">
                              {hasByteData
                                ? <span>{fmtBytes(d.usedBytes)} / {fmtBytes(d.totalBytes)}</span>
                                : <span>{pct.toFixed(1)}% used</span>}
                              <span className="disk-summary-pct">{pct.toFixed(1)}%</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {metricsTimeRange && (
                      <div className="vm-time-range-label">
                        {new Date(metricsTimeRange.startTime).toLocaleString()} — {new Date(metricsTimeRange.endTime).toLocaleString()}
                      </div>
                    )}

                    {metricsLoading ? (
                      <div className="loading-spinner"><div className="spinner"></div></div>
                    ) : !metrics || Object.keys(metrics).length === 0 ? (
                      <div className="empty-state">
                        <HiOutlineChartBar size={48} style={{ color: 'var(--text-muted)', marginBottom: 12 }} />
                        <h3>No metrics available</h3>
                        <p>Make sure the GCP Ops Agent is installed on the VM for memory and disk metrics.</p>
                      </div>
                    ) : (
                      <div className="metrics-grid">
                        {Object.entries(metrics).map(([name, data]) => <VMMetricCard key={name} name={name} data={data} vmKey={vmKey} />)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {vmInstances.length === 0 && (
            <div className="empty-state" style={{ marginTop: 24 }}>
              <HiOutlineServer size={48} style={{ color: 'var(--text-muted)', marginBottom: 12 }} />
              <h3>No VM Instances Added</h3>
              <p>Click "Add VM Instance" to select a region and instance to monitor</p>
            </div>
          )}
          </>
          )}
        </div>
      )}

      {activeTab === 'backup' && (
        <div className="backup-section">
          {!activeProject ? (
            <div className="empty-state" style={{ marginTop: 40 }}>
              <HiOutlineArchive size={48} style={{ color: 'var(--text-muted)', marginBottom: 12 }} />
              <h3>Select a GCP Project</h3>
              <p>Use the project selector above to choose a specific GCP project</p>
            </div>
          ) : (
            <>
              <div className="resource-action-bar">
                <button className="btn btn-primary btn-sm" onClick={() => { setShowBucketModal(true); fetchBuckets(); }}>
                  <HiOutlinePlus size={14} /> Add Backup Bucket
                </button>
              </div>

              {addedBuckets.map((bkt) => {
                const bktKey = bkt.value;
                const isExpanded = expandedBuckets[bktKey];
                const bucketLatest = bucketLatestMap[bktKey];
                const isLoading = bucketLatestLoadingMap[bktKey];

                return (
                  <div key={bktKey} className="expandable-resource-card card">
                    <div className="expandable-resource-header" onClick={() => toggleBucket(bktKey)}>
                      <div className="expandable-resource-info">
                        <HiOutlineArchive size={18} className="added-resource-icon" />
                        <div className="added-resource-details">
                          <span className="added-resource-name">{bkt.value}</span>
                          <span className="added-resource-meta">{bkt.label}</span>
                        </div>
                      </div>
                      <div className="expandable-resource-actions">
                        <button className="btn btn-secondary btn-sm" onClick={(e) => { e.stopPropagation(); fetchBucketLatest(bkt); }} disabled={isLoading} title="Refresh">
                          <HiOutlineRefresh size={14} className={isLoading ? 'spin' : ''} />
                        </button>
                        <button className="btn btn-danger btn-sm" onClick={(e) => { e.stopPropagation(); handleRemoveBucket(bkt); }} title="Remove">
                          <HiOutlineTrash size={14} />
                        </button>
                        <HiOutlineChevronDown size={16} className={`expandable-chevron ${isExpanded ? 'expanded' : ''}`} />
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="expandable-resource-body">
                        {isLoading && (
                          <div className="loading-inline"><div className="spinner" style={{ width: 18, height: 18 }}></div><span>Fetching latest file...</span></div>
                        )}

                        {bucketLatest && !isLoading && (
                          <div className="backup-info-section">
                            <div className="backup-info-grid">
                              <div className="backup-info-item">
                                <span className="backup-info-label">Bucket Name</span>
                                <span className="backup-info-value">{bucketLatest.bucketName}</span>
                              </div>
                              <div className="backup-info-item">
                                <span className="backup-info-label">Total Files</span>
                                <span className="backup-info-value">{bucketLatest.totalFiles}</span>
                              </div>
                              {bucketLatest.latestFile ? (
                                <>
                                  <div className="backup-info-item full-width">
                                    <span className="backup-info-label">Last Stored File</span>
                                    <div className="file-download-row">
                                      <span className="backup-info-value mono">{bucketLatest.latestFile.name}</span>
                                      <button
                                        className="download-btn"
                                        onClick={() => handleDownloadFile(bkt)}
                                        disabled={downloading === bktKey}
                                        title="Download this file"
                                      >
                                        <HiOutlineDownload size={15} />
                                        <span>{downloading === bktKey ? 'Generating...' : 'Download'}</span>
                                      </button>
                                    </div>
                                  </div>
                                  <div className="backup-info-item">
                                    <span className="backup-info-label">Timestamp</span>
                                    <span className="backup-info-value">{new Date(bucketLatest.latestFile.updated).toLocaleString()}</span>
                                  </div>
                                  <div className="backup-info-item">
                                    <span className="backup-info-label">File Size</span>
                                    <span className="backup-info-value">{fmtBytes(bucketLatest.latestFile.size)}</span>
                                  </div>
                                  <div className="backup-info-item">
                                    <span className="backup-info-label">Content Type</span>
                                    <span className="backup-info-value">{bucketLatest.latestFile.contentType || '—'}</span>
                                  </div>
                                </>
                              ) : (
                                <div className="backup-info-item full-width">
                                  <span className="backup-info-label">Status</span>
                                  <span className="backup-info-value" style={{ color: 'var(--accent-red, #ef4444)' }}>No files found in bucket</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {addedBuckets.length === 0 && (
                <div className="empty-state" style={{ marginTop: 16 }}>
                  <HiOutlineArchive size={48} style={{ color: 'var(--text-muted)', marginBottom: 12 }} />
                  <h3>No Backup Buckets Added</h3>
                  <p>Click "Add Backup Bucket" to select a bucket to monitor</p>
                </div>
              )}

              {/* Snapshots Section */}
              <div className="backup-card card">
                <div className="backup-card-header">
                  <div className="backup-card-title">
                    <HiOutlinePhotograph size={20} />
                    <h3>Disk Snapshots</h3>
                  </div>
                  <div className="backup-card-actions">
                    <input
                      type="text"
                      className="snapshot-filter-input"
                      placeholder="Filter by disk name..."
                      value={snapshotDiskFilter}
                      onChange={(e) => setSnapshotDiskFilter(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && fetchSnapshots()}
                    />
                    <button className="btn btn-secondary btn-sm" onClick={fetchSnapshots} disabled={snapshotsLoading}>
                      <HiOutlineRefresh size={14} />
                    </button>
                  </div>
                </div>

                <div className="backup-card-body">
                  {snapshotsLoading ? (
                    <div className="loading-inline"><div className="spinner" style={{ width: 18, height: 18 }}></div><span>Loading snapshots...</span></div>
                  ) : !snapshots ? null : snapshots.totalSnapshots === 0 ? (
                    <div className="snapshot-status-banner no-snapshots">
                      <HiOutlineX size={20} />
                      <div>
                        <strong>No snapshots found</strong>
                        <p>No disk snapshots exist for this project{snapshotDiskFilter ? ` matching "${snapshotDiskFilter}"` : ''}. Snapshots may not be configured.</p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="snapshot-status-banner has-snapshots">
                        <HiOutlineCheck size={20} />
                        <div>
                          <strong>Snapshots Active</strong>
                          <p>{snapshots.totalSnapshots} snapshot{snapshots.totalSnapshots !== 1 ? 's' : ''} found. Last snapshot: <strong>{snapshots.latestSnapshot?.name}</strong></p>
                        </div>
                      </div>

                      {snapshots.latestSnapshot && (
                        <div className="backup-info-grid" style={{ marginTop: 16 }}>
                          <div className="backup-info-item">
                            <span className="backup-info-label">Latest Snapshot</span>
                            <span className="backup-info-value mono">{snapshots.latestSnapshot.name}</span>
                          </div>
                          <div className="backup-info-item">
                            <span className="backup-info-label">Created At</span>
                            <span className="backup-info-value">{new Date(snapshots.latestSnapshot.createdAt).toLocaleString()}</span>
                          </div>
                          <div className="backup-info-item">
                            <span className="backup-info-label">Source Disk</span>
                            <span className="backup-info-value">{snapshots.latestSnapshot.sourceDisk || '—'}</span>
                          </div>
                          <div className="backup-info-item">
                            <span className="backup-info-label">Disk Size</span>
                            <span className="backup-info-value">{snapshots.latestSnapshot.diskSizeGb} GB</span>
                          </div>
                          <div className="backup-info-item">
                            <span className="backup-info-label">Storage Used</span>
                            <span className="backup-info-value">{fmtBytes(snapshots.latestSnapshot.storageBytes)}</span>
                          </div>
                          <div className="backup-info-item">
                            <span className="backup-info-label">Status</span>
                            <span className={`backup-info-value ${snapshots.latestSnapshot.status === 'READY' ? 'status-ok' : 'status-warn'}`}>
                              {snapshots.latestSnapshot.status}
                            </span>
                          </div>
                        </div>
                      )}

                      {snapshots.snapshots?.length > 1 && (
                        <div className="snapshot-history">
                          <h4>Recent Snapshots</h4>
                          <div className="snapshot-table">
                            <div className="snapshot-table-header">
                              <span>Name</span>
                              <span>Source Disk</span>
                              <span>Created</span>
                              <span>Size</span>
                              <span>Status</span>
                            </div>
                            {snapshots.snapshots.map((s) => (
                              <div key={s.name} className="snapshot-table-row">
                                <span className="mono">{s.name}</span>
                                <span>{s.sourceDisk || '—'}</span>
                                <span>{new Date(s.createdAt).toLocaleString()}</span>
                                <span>{fmtBytes(s.storageBytes)}</span>
                                <span className={s.status === 'READY' ? 'status-ok' : 'status-warn'}>{s.status}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Add VM Instance Modal */}
      {showVMModal && (
        <div className="modal-overlay" onClick={() => setShowVMModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2><HiOutlineServer size={22} style={{ verticalAlign: 'middle', marginRight: 8 }} />Add VM Instance</h2>
            <div className="form-group">
              <label>Region</label>
              <Select styles={selectStyles} options={zoneOptions} value={selectedZone} onChange={setSelectedZone} placeholder="Select region..." isClearable isSearchable menuPortalTarget={document.body} menuPosition="fixed" />
            </div>
            <div className="form-group">
              <label>Instance</label>
              {loadingInstances ? (
                <div className="loading-inline"><div className="spinner" style={{ width: 18, height: 18 }}></div><span>Loading instances...</span></div>
              ) : (
                <Select styles={selectStyles} options={instanceOptions} value={instancePick} onChange={setInstancePick} placeholder="Select VM instance..." isClearable isSearchable noOptionsMessage={() => selectedZone ? 'No instances found (or all already added)' : 'Select a region first'} menuPortalTarget={document.body} menuPosition="fixed" />
              )}
            </div>
            <div className="modal-actions">
              <button className="btn btn-primary" disabled={!instancePick} onClick={handleAddVM}>
                Add Instance
              </button>
              <button className="btn btn-secondary" onClick={() => setShowVMModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Bucket Modal */}
      {showBucketModal && (
        <div className="modal-overlay" onClick={() => setShowBucketModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2><HiOutlineArchive size={22} style={{ verticalAlign: 'middle', marginRight: 8 }} />Add Backup Bucket</h2>
            <div className="form-group">
              <label>Bucket</label>
              {bucketsLoading ? (
                <div className="loading-inline"><div className="spinner" style={{ width: 18, height: 18 }}></div><span>Loading buckets...</span></div>
              ) : (
                <Select styles={selectStyles} options={bucketOptions} value={bucketPick} onChange={setBucketPick} placeholder="Select a bucket..." isClearable isSearchable noOptionsMessage={() => 'No buckets found (or all already added)'} menuPortalTarget={document.body} menuPosition="fixed" />
              )}
            </div>
            <div className="modal-actions">
              <button className="btn btn-primary" disabled={!bucketPick} onClick={handleAddBucket}>
                Add Bucket
              </button>
              <button className="btn btn-secondary" onClick={() => setShowBucketModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function VMMetricCard({ name, data, vmKey }) {
  const [hoverIndex, setHoverIndex] = useState(null);
  const [chartType, setChartType] = useState('line');
  const colors = METRIC_COLORS[name] || METRIC_COLORS['CPU Utilization'];
  const gradientId = `vm-grad-${vmKey}-${name.replace(/[^a-zA-Z]/g, '')}`;

  if (!data || data.totalDataPoints === 0) {
    return (
      <div className="card metric-card">
        <div className="mc-header">
          <h3>{name}</h3>
          {data?.unit && <span className="mc-unit">{data.unit}</span>}
        </div>
        <div className="metric-empty">No data points — Ops Agent may not be installed</div>
      </div>
    );
  }

  const points = data.points || [];
  const values = points.map((p) => p.value).filter((v) => v !== null && v !== undefined);
  const maxVal = Math.max(...values, 1e-9);
  const minVal = Math.min(...values);
  const avgVal = values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0;
  const latestVal = values.length > 0 ? values[values.length - 1] : 0;

  const fmtVal = (v) => {
    if (data.unit === '%') return (v * 100).toFixed(1) + '%';
    if (data.unit === 'ms') return v.toFixed(1) + ' ms';
    if (Math.abs(v) >= 1000000) return (v / 1000000).toFixed(2) + 'M';
    if (Math.abs(v) >= 1000) return (v / 1000).toFixed(1) + 'K';
    if (Number.isInteger(v)) return String(v);
    return v.toFixed(2);
  };

  const step = Math.max(1, Math.floor(points.length / 80));
  const sampled = [];
  for (let i = 0; i < points.length; i += step) sampled.push(points[i]);
  if (sampled.length === 0) sampled.push(...points.slice(0, 1));

  const W = 400, H = 100, PAD = 2;
  const chartW = W - PAD * 2, chartH = H - PAD * 2;
  const range = maxVal - minVal || 1;

  const pathPoints = sampled.map((p, i) => {
    const x = PAD + (i / Math.max(sampled.length - 1, 1)) * chartW;
    const y = PAD + chartH - ((p.value - minVal) / range) * chartH;
    return { x, y };
  });

  const linePath = pathPoints.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const areaPath = linePath + ` L${pathPoints[pathPoints.length - 1].x.toFixed(1)},${H} L${pathPoints[0].x.toFixed(1)},${H} Z`;

  const barWidth = Math.max(1, (chartW / sampled.length) * 0.7);
  const barGap = (chartW / sampled.length) * 0.15;

  const hoverPt = hoverIndex !== null && hoverIndex < sampled.length ? sampled[hoverIndex] : null;

  return (
    <div className="card metric-card" style={{ borderTop: `2px solid ${colors.stroke}` }}>
      <div className="mc-header">
        <h3>{name}</h3>
        <div className="mc-header-right">
          {data.unit && <span className="mc-unit">{data.unit}</span>}
          <div className="mc-chart-toggle">
            <button className={`mc-toggle-btn ${chartType === 'bar' ? 'active' : ''}`} onClick={() => setChartType('bar')} title="Bar Chart">
              <svg width="14" height="14" viewBox="0 0 14 14"><rect x="1" y="6" width="3" height="7" rx="0.5" fill="currentColor"/><rect x="5.5" y="3" width="3" height="10" rx="0.5" fill="currentColor"/><rect x="10" y="1" width="3" height="12" rx="0.5" fill="currentColor"/></svg>
            </button>
            <button className={`mc-toggle-btn ${chartType === 'line' ? 'active' : ''}`} onClick={() => setChartType('line')} title="Line Chart">
              <svg width="14" height="14" viewBox="0 0 14 14"><polyline points="1,11 4,6 7,8 10,3 13,5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
          </div>
        </div>
      </div>

      <div className="mc-stats">
        <div className="mc-stat">
          <span className="mc-stat-val" style={{ color: colors.stroke }}>{fmtVal(latestVal)}</span>
          <span className="mc-stat-lbl">Latest</span>
        </div>
        <div className="mc-stat">
          <span className="mc-stat-val">{fmtVal(avgVal)}</span>
          <span className="mc-stat-lbl">Avg</span>
        </div>
        <div className="mc-stat">
          <span className="mc-stat-val">{fmtVal(maxVal)}</span>
          <span className="mc-stat-lbl">Max</span>
        </div>
        <div className="mc-stat">
          <span className="mc-stat-val">{fmtVal(minVal)}</span>
          <span className="mc-stat-lbl">Min</span>
        </div>
      </div>

      {hoverPt && (
        <div className="mc-tooltip">
          <span className="mc-tooltip-val" style={{ color: colors.stroke }}>{fmtVal(hoverPt.value)}</span>
          <span className="mc-tooltip-ts">{new Date(hoverPt.timestamp).toLocaleString()}</span>
        </div>
      )}

      <div className="mc-chart-wrap">
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="mc-svg">
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={colors.gradient[0]} />
              <stop offset="100%" stopColor={colors.gradient[1]} />
            </linearGradient>
          </defs>
          {[0.25, 0.5, 0.75].map((pct) => (
            <line key={pct} x1={PAD} x2={W - PAD} y1={PAD + chartH * (1 - pct)} y2={PAD + chartH * (1 - pct)} stroke="var(--chart-grid)" strokeWidth="0.5" />
          ))}

          {chartType === 'bar' ? (
            <>
              {sampled.map((p, i) => {
                const barX = PAD + (i / Math.max(sampled.length, 1)) * chartW + barGap;
                const barH = ((p.value - minVal) / range) * chartH;
                const barY = PAD + chartH - barH;
                return (
                  <g key={i} onMouseEnter={() => setHoverIndex(i)} onMouseLeave={() => setHoverIndex(null)}>
                    <rect x={barX} y={barY} width={barWidth} height={Math.max(barH, 0.5)} rx="0.5"
                      fill={hoverIndex === i ? colors.stroke : `url(#${gradientId})`}
                      stroke={colors.stroke} strokeWidth="0.3" opacity={hoverIndex === i ? 1 : 0.85} />
                    <rect x={barX} y={0} width={barWidth} height={H} fill="transparent" />
                  </g>
                );
              })}
            </>
          ) : (
            <>
              <path d={areaPath} fill={`url(#${gradientId})`} />
              <path d={linePath} fill="none" stroke={colors.stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              {pathPoints.map((p, i) => (
                <rect key={i} x={p.x - chartW / sampled.length / 2} y={0} width={chartW / sampled.length} height={H} fill="transparent"
                  onMouseEnter={() => setHoverIndex(i)} onMouseLeave={() => setHoverIndex(null)} />
              ))}
              {hoverIndex !== null && pathPoints[hoverIndex] && (
                <>
                  <line x1={pathPoints[hoverIndex].x} x2={pathPoints[hoverIndex].x} y1={PAD} y2={H} stroke="var(--alpha-white-15)" strokeWidth="0.5" strokeDasharray="3,2" />
                  <circle cx={pathPoints[hoverIndex].x} cy={pathPoints[hoverIndex].y} r="3" fill={colors.stroke} stroke="var(--bg-primary)" strokeWidth="1.5" />
                </>
              )}
            </>
          )}
        </svg>
      </div>

      <div className="mc-footer">
        <span>{points.length > 0 ? new Date(points[0].timestamp).toLocaleString() : ''}</span>
        <span>{data.totalDataPoints} pts</span>
        <span>{points.length > 0 ? new Date(points[points.length - 1].timestamp).toLocaleString() : ''}</span>
      </div>
    </div>
  );
}

export default MongoObservability;
